'use strict';
import UIIcon from '@icons/UIIcon.js';
import { SpatialVisionState } from '@states/SpatialVision.js';
import { UIButton, UIDiv, UIText } from '@ui';
import SpatialVisionSessionResult from './SpatialVisionSessionResult.js';

const isRunning = session =>
  session.status === 'running' || session.status === 'partial';

const conditionResults = session =>
  (
    session?.conditionResults ||
    session?.resultMetadata?.conditionResults ||
    session?.resultMetadata?.condition_results ||
    []
  ).filter(result => result && typeof result === 'object');

const HIDDEN_RESULT_STATUSES = new Set(['matched', 'not matched', 'uncertain']);

const formatStatusValue = value =>
  String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const shouldHideResultStatus = status =>
  HIDDEN_RESULT_STATUSES.has(formatStatusValue(status).toLowerCase());

class SpatialVisionBatchResult {
  constructor({ showHeader = true, showAutomatedTagsExport = false } = {}) {
    this.showHeader = showHeader;
    this.showAutomatedTagsExport = showAutomatedTagsExport;
  }

  render(sessions = []) {
    const sorted = sessions
      .filter(Boolean)
      .slice()
      .sort(
        (a, b) =>
          Number(a.batchIndex ?? 0) - Number(b.batchIndex ?? 0) ||
          new Date(a.startedAt || a.timestamp || 0) -
            new Date(b.startedAt || b.timestamp || 0),
      );
    const wrapper = new UIDiv().setClass('space-y-3');
    if (!sorted.length) return wrapper;

    if (this.showHeader) wrapper.add(this.#renderHeader(sorted));

    const defaultExpandedIndex = this.#defaultExpandedIndex(sorted);
    sorted.forEach((session, index) => {
      wrapper.add(
        this.#renderImageCard(
          session,
          index,
          sorted.length,
          index === defaultExpandedIndex,
        ),
      );
    });

    return wrapper;
  }

  #renderHeader(sessions) {
    const batchId = sessions[0]?.batchId;
    const runningCount = sessions.filter(isRunning).length;
    const completedCount = sessions.filter(
      session => session.status === 'completed',
    ).length;
    const failedCount = sessions.filter(
      session => session.status === 'failed',
    ).length;
    const header = new UIDiv()
      .setClass(
        'rounded-lg border border-blue-100 bg-blue-50 px-3 py-3 text-sm text-blue-900',
      )
      .add(
        new UIDiv().setClass('flex items-center justify-between gap-3').add(
          new UIDiv()
            .setClass('min-w-0')
            .add(
              new UIText('O-HIVE VLM batch detection').setClass(
                'block font-semibold',
              ),
              new UIText(
                `${completedCount}/${sessions.length} complete${
                  runningCount ? `, ${runningCount} running` : ''
                }${failedCount ? `, ${failedCount} failed` : ''}`,
              ).setClass('block text-xs text-blue-700'),
            ),
          runningCount && batchId
            ? new UIButton()
                .setClass(
                  'flex shrink-0 items-center gap-1.5 rounded-full border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-600 transition-colors hover:bg-red-50',
                )
                .add(
                  new UIIcon('circle-cross', {
                    color: 'none',
                    strokeColor: 'currentColor',
                    size: 14,
                  }),
                  new UIText('Cancel all'),
                )
                .onClick(async e => {
                  e.stopPropagation();
                  await SpatialVisionState.cancelBatch(batchId);
                })
            : new UIDiv().setClass('hidden'),
        ),
      );

    return header;
  }

  #renderImageCard(session, index, total, initiallyExpanded) {
    let expanded = initiallyExpanded;
    const content = new UIDiv();
    const chevron = new UIIcon('dropdown', {
      color: 'none',
      strokeColor: 'currentColor',
      size: 14,
    }).setClass(
      initiallyExpanded
        ? 'rotate-180 transition-transform'
        : 'transition-transform',
    );
    const card = new UIDiv().setClass(
      'rounded-lg border border-gray-200 bg-white overflow-hidden',
    );
    const header = new UIButton()
      .setClass(
        'flex w-full items-center justify-between gap-3 bg-gray-50/70 px-3 py-2.5 text-left transition-colors hover:bg-gray-100',
      )
      .add(
        new UIDiv()
          .setClass('min-w-0 flex items-center gap-3')
          .add(
            this.#thumbnail(session),
            new UIDiv()
              .setClass('min-w-0')
              .add(
                new UIText(
                  session.name ||
                    `Image ${Number(session.batchIndex ?? index) + 1} of ${
                      session.batchTotal || total
                    }`,
                ).setClass(
                  'block truncate text-sm font-semibold text-slate-900',
                ),
                new UIText(this.#imageCardSummary(session)).setClass(
                  'block truncate text-xs text-slate-500',
                ),
              ),
          ),
        new UIDiv()
          .setClass('flex shrink-0 items-center gap-2')
          .add(
            new UIText(this.#statusLabel(session)).setClass(
              `rounded-full px-2 py-0.5 text-xs font-semibold ${this.#statusPillClass(
                session,
              )}`,
            ),
            chevron,
          ),
      )
      .onClick(() => {
        expanded = !expanded;
        chevron.setClass(
          expanded ? 'rotate-180 transition-transform' : 'transition-transform',
        );
        this.#renderImageCardContent(content, session, expanded);
      });

    card.add(header, content);
    this.#renderImageCardContent(content, session, expanded);
    return card;
  }

  #renderImageCardContent(container, session, expanded) {
    container.clear();
    if (!expanded) {
      const answer = session.llmOutput || session.inspectionRationale || '';
      if (answer) {
        container.add(
          new UIText(answer).setClass(
            'block px-3 pb-3 text-sm leading-relaxed text-slate-600',
          ),
        );
      }
      return;
    }
    container.add(
      new UIDiv().setClass('p-2').add(
        new SpatialVisionSessionResult({
          showUserMessage: false,
          showAutomatedTagsExport: this.showAutomatedTagsExport,
        }).render(session),
      ),
    );
  }

  #thumbnail(session) {
    const url = session.outputImageUrl || session.inputImageUrl;
    if (!url) {
      return new UIDiv().setClass(
        'h-10 w-10 shrink-0 rounded-md border border-gray-200 bg-gray-100',
      );
    }
    const wrap = new UIDiv().setClass(
      'h-10 w-10 shrink-0 overflow-hidden rounded-md border border-gray-200 bg-gray-100',
    );
    const img = document.createElement('img');
    img.src = url;
    img.alt = 'Spatial Vision result thumbnail';
    img.className = 'h-full w-full object-cover';
    wrap.dom.appendChild(img);
    return wrap;
  }

  #imageCardSummary(session) {
    const results = conditionResults(session);
    if (!results.length) {
      const detections = session.detections?.length || 0;
      return `${detections} detection${detections === 1 ? '' : 's'}`;
    }
    const conditionCount = results.length;
    const status =
      session.inspectionStatus &&
      !shouldHideResultStatus(session.inspectionStatus)
        ? `${formatStatusValue(session.inspectionStatus)} · `
        : '';
    return `${status}${conditionCount} check${conditionCount === 1 ? '' : 's'}`;
  }

  #defaultExpandedIndex(sessions) {
    const problematic = sessions.findIndex(session => {
      const results = conditionResults(session);
      return (
        session.status !== 'completed' ||
        results.some(result =>
          ['matched', 'uncertain'].includes(result.resultStatus),
        )
      );
    });
    return problematic >= 0 ? problematic : 0;
  }

  #statusLabel(session) {
    if (isRunning(session)) return 'Running';
    if (session.status === 'failed') return 'Failed';
    if (session.status === 'canceled') return 'Canceled';
    return 'Completed';
  }

  #statusClass(session) {
    if (isRunning(session)) return 'text-blue-600';
    if (session.status === 'failed') return 'text-red-600';
    if (session.status === 'canceled') return 'text-slate-500';
    return 'text-green-600';
  }

  #statusPillClass(session) {
    if (isRunning(session)) return 'bg-blue-50 text-blue-700';
    if (session.status === 'failed') return 'bg-red-50 text-red-700';
    if (session.status === 'canceled') return 'bg-slate-100 text-slate-600';
    return 'bg-green-50 text-green-700';
  }
}

export default SpatialVisionBatchResult;
